import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMasterPartnerAuth } from '../contexts/MasterPartnerAuthContext';
import { 
  Users, FileText, TrendingUp, DollarSign, Copy, Check, ExternalLink,
  LogOut, Menu, X, Home, BarChart2, Settings, RefreshCw, Eye, EyeOff,
  Loader2, ChevronRight, Clock, CheckCircle, XCircle, AlertCircle, IndianRupee
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterPartnerDashboard = () => {
  const navigate = useNavigate();
  const { masterPartner, logout, getAuthHeader, refreshProfile } = useMasterPartnerAuth();
  
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [togglingLink, setTogglingLink] = useState(false);
  const [regeneratingCode, setRegeneratingCode] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/master-partner/portal/dashboard`, {
        headers: getAuthHeader()
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/partner/signup/${dashboard?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleReferralLink = async () => {
    setTogglingLink(true);
    try {
      await axios.put(`${API}/master-partner/portal/referral-link/toggle`, {}, {
        headers: getAuthHeader()
      });
      await fetchDashboard();
      await refreshProfile();
    } catch (error) {
      console.error('Error toggling referral link:', error);
    } finally {
      setTogglingLink(false);
    }
  };

  const regenerateReferralCode = async () => {
    if (!window.confirm('Are you sure you want to regenerate your referral code? The old link will stop working.')) return;
    
    setRegeneratingCode(true);
    try {
      await axios.post(`${API}/master-partner/portal/referral-link/regenerate`, {}, {
        headers: getAuthHeader()
      });
      await fetchDashboard();
      await refreshProfile();
    } catch (error) {
      console.error('Error regenerating code:', error);
    } finally {
      setRegeneratingCode(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/master-partner/login');
  };

  const stats = dashboard?.stats || {};
  const referralLink = `${window.location.origin}/partner/signup/${dashboard?.referral_code}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b">
          <h1 className="text-xl font-bold text-gray-900">Master Partner</h1>
          <p className="text-sm text-gray-500 truncate">{masterPartner?.company_name}</p>
        </div>
        
        <nav className="p-4 space-y-2">
          <Link 
            to="/master-partner/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-purple-50 text-purple-700 font-medium"
          >
            <Home className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/master-partner/partners" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Users className="w-5 h-5" />
            My Partners
          </Link>
          <Link 
            to="/master-partner/commissions" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <IndianRupee className="w-5 h-5" />
            Commissions
          </Link>
          {dashboard?.can_create_leads && (
            <Link 
              to="/master-partner/leads" 
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <FileText className="w-5 h-5" />
              My Leads
            </Link>
          )}
          <Link 
            to="/master-partner/profile" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-5 h-5" />
            Profile
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1 lg:flex-none">
              <h2 className="text-lg font-semibold text-gray-900">Dashboard</h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchDashboard}
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Status Banner for pending/incomplete setup */}
          {masterPartner && (masterPartner.status !== 'approved' || !masterPartner.can_recruit) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">Complete Your Setup</h4>
                  <p className="text-amber-700 text-sm mt-1">
                    To start recruiting partners, please complete the following:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {masterPartner.status === 'pending' && (
                      <li className="flex items-center gap-2 text-amber-700">
                        <Clock className="w-4 h-4" />
                        Account approval pending
                      </li>
                    )}
                    {(!masterPartner.documents || masterPartner.documents.length === 0) && (
                      <li className="flex items-center gap-2 text-amber-700">
                        <XCircle className="w-4 h-4" />
                        <Link to="/master-partner/profile" className="underline hover:text-amber-800">
                          Upload required documents
                        </Link>
                      </li>
                    )}
                    {masterPartner.documents?.length > 0 && !masterPartner.documents_approved && (
                      <li className="flex items-center gap-2 text-amber-700">
                        <Clock className="w-4 h-4" />
                        Documents pending approval
                      </li>
                    )}
                    {!masterPartner.agreement_accepted && (
                      <li className="flex items-center gap-2 text-amber-700">
                        <XCircle className="w-4 h-4" />
                        <Link to="/master-partner/profile" className="underline hover:text-amber-800">
                          Accept the partner agreement
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Referral Link Card */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-6 mb-6 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold mb-1">Your Partner Referral Link</h3>
                <p className="text-purple-100 text-sm">Share this link with partners to recruit them under your network</p>
                {!masterPartner?.can_recruit && (
                  <p className="text-amber-300 text-xs mt-2">⚠️ Link is disabled until your setup is complete</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleReferralLink}
                  disabled={togglingLink || !masterPartner?.can_recruit}
                  className={`p-2 rounded-lg transition-colors ${
                    !masterPartner?.can_recruit
                      ? 'bg-gray-500/30 cursor-not-allowed'
                      : dashboard?.public_link_enabled 
                        ? 'bg-white/20 hover:bg-white/30' 
                        : 'bg-red-500/30 hover:bg-red-500/40'
                  }`}
                  title={!masterPartner?.can_recruit ? 'Complete setup to enable' : dashboard?.public_link_enabled ? 'Disable link' : 'Enable link'}
                >
                  {togglingLink ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : dashboard?.public_link_enabled ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <EyeOff className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={regenerateReferralCode}
                  disabled={regeneratingCode}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                  title="Regenerate code"
                >
                  {regeneratingCode ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 bg-white/10 rounded-lg px-4 py-3 font-mono text-sm truncate">
                {dashboard?.public_link_enabled ? referralLink : '(Link disabled)'}
              </div>
              <button
                onClick={copyReferralLink}
                disabled={!dashboard?.public_link_enabled}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    Copy Link
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-total-partners">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Partners</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total_partners || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm">
                <span className="text-green-600">{stats.approved_partners || 0} approved</span>
                <span className="text-amber-600">{stats.pending_partners || 0} pending</span>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-total-leads">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total_leads || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-500">
                From all recruited partners
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-combined-revenue">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Combined Revenue</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">₹{(stats.combined_revenue || 0).toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-500">
                Total invoiced amount
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-commission">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Commission Earned</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">₹{(stats.total_commission_earned || 0).toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <div className="mt-4 text-sm">
                <span className="text-green-600">₹{(stats.total_commission_paid || 0).toLocaleString()} paid</span>
              </div>
            </div>
          </div>

          {/* Lead Stages & Recent Partners */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lead Stage Breakdown */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Lead Stages</h3>
              </div>
              <div className="p-6">
                {Object.keys(stats.lead_stages || {}).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(stats.lead_stages || {}).map(([stage, count]) => (
                      <div key={stage} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">{stage.replace('_', ' ')}</span>
                        <span className="font-semibold text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <BarChart2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No lead data yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Partners */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Recent Partners</h3>
                <Link 
                  to="/master-partner/partners"
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  View All
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="divide-y">
                {(dashboard?.recent_partners || []).length > 0 ? (
                  dashboard.recent_partners.map((partner) => (
                    <div key={partner.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="font-medium text-gray-900">{partner.company_name}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(partner.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        partner.status === 'approved' 
                          ? 'bg-green-100 text-green-700'
                          : partner.status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : partner.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {partner.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No partners yet</p>
                    <p className="text-sm mt-1">Share your referral link to recruit partners</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MasterPartnerDashboard;
